/**
 * Admin Amendment Modal
 *
 * Allows admins to edit orders with full product/cargo/quantity control.
 * Matches the web customer amendment UI for consistency.
 */

import * as React from "react";
import { useQuery, useMutation } from "convex/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";

import {
  Plus,
  Trash2,
  RefreshCcw,
  AlertTriangle,
  Check,
  Loader2,
  ChevronsUpDown,
  Clock,
} from "lucide-react";
import { api } from "@/lib/convexApi";
import { formatCurrency } from "@/lib/format";
import { DEFAULT_PRODUCT_IMAGE, cleanStatus } from "@/lib/product";
import { cn } from "@/lib/utils";
import type { Id } from "$convex/_generated/dataModel";

type Step = "select" | "preview" | "confirm" | "submitting" | "done";

type OrderItem = {
  _id: string;
  productId?: string | null;
  cargoId?: string | null;
  product?: {
    name?: string;
    slug?: string | null;
  } | null;
  cargo?: {
    ociNumber?: string;
    contractId?: string;
    status?: string;
  } | null;
  quantity: number;
  unitPrice: number;
  unitWeight: number;
};

type CatalogProduct = {
  id: string;
  name: string;
  slug: string | null;
  origin: string | null;
  description: string | null;
  millingProcess: string | null;
  imageUrl: string | null;
  cargoStatus: string | null;
  productCost: number | null;
  availableBags: number;
  requiresContactForPricing?: boolean;
};

type CargoOption = {
  id: string;
  status: string;
  availableBags: number;
  isContracted: boolean;
  arrivalDate?: string;
  ociNumber?: string;
};

type DraftLine = {
  draftId: string;
  orderItemId?: string;
  originalProductId: string | null;
  originalCargoId: string | null;
  originalProductName: string;
  originalProductSlug: string | null;
  originalQuantity: number;
  quantity: number;
  removed: boolean;
  isNew: boolean;
  isSwapping: boolean;
  selectedProductId: string | null;
  selectedProductName: string;
  selectedProductSlug: string | null;
  selectedCargoId: string | null;
};

type PreviewChange = {
  kind: "added" | "removed" | "quantity_changed" | "swapped";
  before: {
    productName: string;
    quantity: number;
    lineTotal: number;
  } | null;
  after: {
    productName: string;
    quantity: number;
    lineTotal: number;
  } | null;
};

type PreviewData = {
  before: {
    subtotal: number;
    shippingCost: number;
    totalAmount: number;
  };
  after: {
    subtotal: number;
    shippingCost: number;
    totalAmount: number;
  };
  itemDelta: number;
  shippingDelta: number;
  totalDelta: number;
  lineChanges?: PreviewChange[];
};

interface AdminAmendmentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  orderId: string;
  orderItems: OrderItem[];
  authUserId: string;
  orderNumber?: string;
}

function createDraftId() {
  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeCargos(cargoOfProduct: Array<Record<string, unknown>>): CargoOption[] {
  if (!Array.isArray(cargoOfProduct)) return [];

  return cargoOfProduct.map((cargo) => {
    const cargoId = cargo._id ? String(cargo._id) : String(cargo.id ?? "");
    const rawArrivalDate = cargo.arrivalDate;
    const arrivalDate =
      typeof rawArrivalDate === "number"
        ? new Date(rawArrivalDate).toISOString().slice(0, 10)
        : typeof rawArrivalDate === "string"
          ? rawArrivalDate.slice(0, 10)
          : undefined;

    return {
      id: cargoId,
      status: typeof cargo.status === "string" ? cargo.status : "available",
      availableBags: Number(cargo.availableBags || 0),
      isContracted: Boolean(cargo.isContracted),
      arrivalDate,
      ociNumber:
        typeof cargo.ociNumber === "string"
          ? cargo.ociNumber
          : typeof cargo.OCI_Number === "string"
            ? cargo.OCI_Number
            : undefined,
    };
  });
}

function formatCargoOptionLabel(cargo: CargoOption) {
  const parts = [cargo.status.replace(/_/g, " "), `${cargo.availableBags} bags`];
  if (cargo.ociNumber) {
    parts.push(`OCI ${cargo.ociNumber}`);
  }
  if (cargo.arrivalDate) {
    parts.push(`Arrives ${cargo.arrivalDate}`);
  }
  if (cargo.isContracted) {
    parts.push("Contract");
  }
  return parts.join(" - ");
}

function formatPriceLabel(price: number) {
  return `$${price.toFixed(2)}/lb`;
}

function getProductPriceLabel(product: CatalogProduct) {
  if (product.requiresContactForPricing) return "Contact for pricing";
  if (product.productCost === null) return null;
  return formatPriceLabel(product.productCost);
}

function getProductStatusBadgeClass(status: string | null) {
  switch (status) {
    case "in_spot":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "at_origin":
      return "border-amber-200 bg-amber-50 text-amber-700";
    case "in_transit":
      return "border-sky-200 bg-sky-50 text-sky-700";
    default:
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
}

function getProductSubtitle(product: CatalogProduct) {
  return [product.origin, product.millingProcess].filter(Boolean).join(" • ");
}

type ProductPickerProps = {
  value: string | null;
  products: CatalogProduct[];
  productsLoading: boolean;
  onValueChange: (value: string) => void;
};

function ProductPicker({ value, products, productsLoading, onValueChange }: ProductPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const selectedProduct = products.find((product) => product.id === value) ?? null;
  const selectedSubtitle = selectedProduct ? getProductSubtitle(selectedProduct) : "";
  const selectedPrice = selectedProduct ? getProductPriceLabel(selectedProduct) : null;

  React.useEffect(() => {
    if (!open) return;
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [open, search]);

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setSearch("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-auto min-h-11 w-full justify-between px-3 py-2 font-normal"
        >
          {selectedProduct ? (
            <div className="flex min-w-0 items-center gap-3 text-left">
              <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                <img
                  src={selectedProduct.imageUrl || DEFAULT_PRODUCT_IMAGE}
                  alt={selectedProduct.name}
                  className="h-full w-full object-cover"
                  onError={(event) => {
                    if ((event.currentTarget as HTMLImageElement).src !== DEFAULT_PRODUCT_IMAGE) {
                      (event.currentTarget as HTMLImageElement).src = DEFAULT_PRODUCT_IMAGE;
                    }
                  }}
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-slate-900">{selectedProduct.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {[selectedSubtitle, selectedProduct.cargoStatus ? cleanStatus(selectedProduct.cargoStatus) : null]
                    .filter(Boolean)
                    .join(" • ")}
                </div>
              </div>
              {selectedPrice && (
                <span className="hidden shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 sm:inline-flex">
                  {selectedPrice}
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">
              {productsLoading ? "Loading products..." : "Select a product"}
            </span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[320px] overflow-hidden p-0" align="start">
        <Command>
          <CommandInput
            placeholder={productsLoading ? "Loading products..." : "Search products..."}
            value={search}
            onValueChange={setSearch}
          />
          <div
            ref={scrollContainerRef}
            className="max-h-[min(22rem,55vh)] overflow-y-auto overscroll-contain"
            onWheelCapture={(event) => event.stopPropagation()}
            onTouchMoveCapture={(event) => event.stopPropagation()}
          >
            <CommandList className="max-h-none overflow-visible">
              <CommandEmpty>No matching coffees found.</CommandEmpty>
              <CommandGroup heading="Available Coffees">
                {products.map((product) => {
                  const subtitle = getProductSubtitle(product);
                  const priceLabel = getProductPriceLabel(product);
                  const isSelected = product.id === value;

                  return (
                    <CommandItem
                      key={product.id}
                      value={[
                        product.name,
                        product.origin,
                        product.millingProcess,
                        product.description,
                        product.cargoStatus,
                        priceLabel,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      disabled={product.requiresContactForPricing}
                      onSelect={() => {
                        onValueChange(product.id);
                        setOpen(false);
                      }}
                      className="items-start gap-3 px-3 py-3"
                    >
                      <div className="mt-0.5 h-12 w-12 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={product.imageUrl || DEFAULT_PRODUCT_IMAGE}
                          alt={product.name}
                          className="h-full w-full object-cover"
                          onError={(event) => {
                            if ((event.currentTarget as HTMLImageElement).src !== DEFAULT_PRODUCT_IMAGE) {
                              (event.currentTarget as HTMLImageElement).src = DEFAULT_PRODUCT_IMAGE;
                            }
                          }}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-900">{product.name}</div>
                            {subtitle && <div className="truncate text-xs text-muted-foreground">{subtitle}</div>}
                          </div>
                          {priceLabel && (
                            <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                              {priceLabel}
                            </span>
                          )}
                        </div>
                        {product.description && (
                          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{product.description}</p>
                        )}
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {product.cargoStatus && (
                            <Badge
                              variant="outline"
                              className={cn("whitespace-nowrap border", getProductStatusBadgeClass(product.cargoStatus))}
                            >
                              {cleanStatus(product.cargoStatus)}
                            </Badge>
                          )}
                          <Badge variant="outline" className="whitespace-nowrap border-slate-200 bg-white text-slate-700">
                            {product.availableBags} bags
                          </Badge>
                          {product.requiresContactForPricing && (
                            <Badge
                              variant="outline"
                              className="whitespace-nowrap border-amber-200 bg-amber-50 text-amber-700"
                            >
                              Call for price
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Check className={cn("mt-1 h-4 w-4 shrink-0 text-primary", isSelected ? "opacity-100" : "opacity-0")} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function getEffectiveProductId(line: DraftLine) {
  return line.isNew || line.isSwapping ? line.selectedProductId : line.originalProductId;
}

function getEffectiveCargoId(line: DraftLine) {
  return line.isNew || line.isSwapping ? line.selectedCargoId : line.originalCargoId;
}

function getEffectiveProductName(line: DraftLine) {
  return line.isNew || line.isSwapping
    ? line.selectedProductName || line.originalProductName
    : line.originalProductName;
}

function getEffectiveProductSlug(line: DraftLine) {
  return line.isNew || line.isSwapping ? line.selectedProductSlug : line.originalProductSlug;
}

function isWholePositiveNumber(value: number) {
  return Number.isInteger(value) && value > 0;
}

function getLineChangeKind(line: DraftLine): "added" | "removed" | "quantity_changed" | "swapped" | null {
  if (line.removed) return line.isNew ? null : "removed";
  if (line.isNew) return "added";

  const productChanged = getEffectiveProductId(line) !== line.originalProductId;
  const cargoChanged = getEffectiveCargoId(line) !== line.originalCargoId;
  if (productChanged || cargoChanged) return "swapped";
  if (line.quantity !== line.originalQuantity) return "quantity_changed";
  return null;
}

function getChangeCounts(lines: DraftLine[]) {
  return lines.reduce(
    (counts, line) => {
      const changeKind = getLineChangeKind(line);
      if (!changeKind) return counts;
      counts[changeKind] += 1;
      return counts;
    },
    { added: 0, removed: 0, swapped: 0, quantity_changed: 0 },
  );
}

type AmendmentLineEditorProps = {
  line: DraftLine;
  products: CatalogProduct[];
  productsLoading: boolean;
  onUpdateLine: (draftId: string, patch: Partial<DraftLine>) => void;
  onRemoveLine: (draftId: string) => void;
  onRestoreLine: (draftId: string) => void;
  onCancelSwap: (draftId: string) => void;
};

function AmendmentLineEditor({
  line,
  products,
  productsLoading,
  onUpdateLine,
  onRemoveLine,
  onRestoreLine,
  onCancelSwap,
}: AmendmentLineEditorProps) {
  const selectedProductSlug = getEffectiveProductSlug(line);
  const productData = useQuery(
    api.productPage.getProductPageData,
    (line.isNew || line.isSwapping) && selectedProductSlug
      ? { slug: selectedProductSlug }
      : "skip",
  ) as { cargoOfProduct?: Array<Record<string, unknown>> } | undefined;

  const cargoOptions = React.useMemo(
    () => normalizeCargos((productData?.cargoOfProduct ?? []) as Array<Record<string, unknown>>),
    [productData?.cargoOfProduct],
  );

  React.useEffect(() => {
    if (!(line.isNew || line.isSwapping)) return;
    if (!line.selectedProductId || cargoOptions.length === 0) return;

    const hasSelectedCargo =
      line.selectedCargoId !== null &&
      cargoOptions.some((cargo) => cargo.id === line.selectedCargoId);

    if (!hasSelectedCargo) {
      onUpdateLine(line.draftId, { selectedCargoId: cargoOptions[0]?.id ?? null });
    }
  }, [
    cargoOptions,
    line.draftId,
    line.isNew,
    line.isSwapping,
    line.selectedCargoId,
    line.selectedProductId,
    onUpdateLine,
  ]);

  const showCatalogControls = line.isNew || line.isSwapping;
  const changeKind = getLineChangeKind(line);

  if (line.removed) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-600 text-white">Will Remove</Badge>
              <span className="text-sm font-medium text-amber-900">{line.originalProductName}</span>
            </div>
            <p className="mt-1 text-sm text-amber-800">
              {line.originalQuantity} bag{line.originalQuantity === 1 ? "" : "s"} will be removed when you save.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => onRestoreLine(line.draftId)}>
            Undo Remove
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">{getEffectiveProductName(line)}</span>
            {line.isNew && <Badge className="bg-emerald-600 text-white">New Item</Badge>}
            {changeKind === "swapped" && <Badge className="bg-emerald-600 text-white">Will Swap</Badge>}
            {changeKind === "quantity_changed" && (
              <Badge className="bg-slate-700 text-white">Qty Changed</Badge>
            )}
          </div>

          {!line.isNew && (
            <p className="mt-1 text-xs text-muted-foreground">
              Current item: {line.originalProductName} - {line.originalQuantity} bag
              {line.originalQuantity === 1 ? "" : "s"}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {!line.isNew && !line.isSwapping && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                onUpdateLine(line.draftId, {
                  isSwapping: true,
                  selectedProductId: line.originalProductId,
                  selectedProductName: line.originalProductName,
                  selectedProductSlug: line.originalProductSlug,
                  selectedCargoId: null,
                })
              }
            >
              <RefreshCcw className="mr-2 h-4 w-4" />
              Swap Item
            </Button>
          )}

          {!line.isNew && line.isSwapping && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onCancelSwap(line.draftId)}>
              Keep Original
            </Button>
          )}

          <Button type="button" variant="ghost" size="sm" onClick={() => onRemoveLine(line.draftId)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Remove
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_120px]">
        {showCatalogControls ? (
          <>
            <div className="min-w-0 space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Product</Label>
              <ProductPicker
                value={line.selectedProductId}
                products={products}
                productsLoading={productsLoading}
                onValueChange={(value) => {
                  const nextProduct = products.find((product) => product.id === value);
                  onUpdateLine(line.draftId, {
                    selectedProductId: value,
                    selectedProductName: nextProduct?.name ?? "",
                    selectedProductSlug: nextProduct?.slug ?? null,
                    selectedCargoId: null,
                  });
                }}
              />
            </div>

            <div className="min-w-0 space-y-2 md:col-span-1 lg:col-auto">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Source Option</Label>
              <Select
                value={line.selectedCargoId ?? undefined}
                onValueChange={(value) => onUpdateLine(line.draftId, { selectedCargoId: value })}
                disabled={!line.selectedProductId || cargoOptions.length === 0}
              >
                <SelectTrigger className="min-w-0 w-full [&>span]:truncate">
                  <SelectValue
                    placeholder={
                      !line.selectedProductId
                        ? "Select a product first"
                        : productData === undefined
                          ? "Loading sources..."
                          : cargoOptions.length === 0
                            ? "No sources available"
                            : "Select a source"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {cargoOptions.map((cargo) => (
                    <SelectItem key={cargo.id} value={cargo.id}>
                      {formatCargoOptionLabel(cargo)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        ) : (
          <div className="space-y-2 md:col-span-2 lg:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Item</Label>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              Adjust the quantity or use Swap Item to replace this line with a different coffee.
            </div>
          </div>
        )}

        <div className="min-w-0 space-y-2 md:max-w-[140px] lg:max-w-none">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Quantity</Label>
          <Input
            type="number"
            min={1}
            max={999}
            value={line.quantity}
            onChange={(event) => {
              const parsedValue = Math.round(Number(event.target.value));
              onUpdateLine(line.draftId, {
                quantity: Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 1,
              });
            }}
            className="w-full text-center"
          />
        </div>
      </div>
    </div>
  );
}

export function AdminAmendmentModal({
  open,
  onClose,
  onSuccess,
  orderId,
  orderItems,
  authUserId,
  orderNumber,
}: AdminAmendmentModalProps) {
  const [step, setStep] = React.useState<Step>("select");
  const [lines, setLines] = React.useState<DraftLine[]>([]);
  const [warningAccepted, setWarningAccepted] = React.useState(false);
  const [fulfillmentStoppedConfirmed, setFulfillmentStoppedConfirmed] = React.useState(false);
  const [doneStatus, setDoneStatus] = React.useState<"applied" | "pending_payment" | null>(null);
  const [previewData, setPreviewData] = React.useState<PreviewData | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const dialogContentRef = React.useRef<HTMLDivElement | null>(null);
  const initializedOrderIdRef = React.useRef<string | null>(null);

  const offerListData = useQuery(api.products.getProductsOfferList, open ? {} : "skip") as {
    products?: Array<Record<string, unknown>>;
  } | undefined;

  const applyAmendment = useMutation(api.adminOrderAmendments.applyAmendmentAsAdmin);
  const eligibility = useQuery(
    api.adminOrderAmendments.getAdminAmendmentEligibility,
    open ? { authUserId, orderId: orderId as Id<"orders"> } : "skip",
  ) as { canEdit: boolean; reasonMessage: string | null; requiresFulfillmentConfirmation: boolean } | undefined;
  const requiresFulfillmentConfirmation = eligibility?.requiresFulfillmentConfirmation === true;

  const products = React.useMemo<CatalogProduct[]>(() => {
    const rawProducts = Array.isArray(offerListData?.products)
      ? (offerListData.products as Array<Record<string, unknown>>)
      : [];

    return rawProducts
      .map((product) => ({
        id: String(product.id ?? product._id ?? ""),
        name: typeof product.name === "string" ? product.name : "",
        slug: typeof product.slug === "string" ? product.slug : null,
        origin: typeof product.origin === "string" ? product.origin : null,
        description: typeof product.description === "string" ? product.description : null,
        millingProcess: typeof product.millingProcess === "string" ? product.millingProcess : null,
        imageUrl:
          product.image_metadata &&
          typeof product.image_metadata === "object" &&
          product.image_metadata !== null &&
          typeof (product.image_metadata as { url?: unknown }).url === "string"
            ? ((product.image_metadata as { url: string }).url ?? null)
            : null,
        cargoStatus: typeof product.cargoStatus === "string" ? product.cargoStatus : null,
        productCost:
          typeof product.productCost === "number"
            ? product.productCost
            : typeof product.productCost === "string"
              ? Number.parseFloat(product.productCost)
              : null,
        availableBags: Number(product.totalAvailableBags ?? product.availableBags ?? 0),
        requiresContactForPricing: product.requiresContactForPricing === true,
      }))
      .filter((product) => product.id && product.name && product.slug);
  }, [offerListData?.products]);

  const activeLines = React.useMemo(() => lines.filter((line) => !line.removed), [lines]);
  const changeCounts = React.useMemo(() => getChangeCounts(lines), [lines]);
  const hasChanges = React.useMemo(() => lines.some((line) => getLineChangeKind(line) !== null), [lines]);
  const allRemoved = activeLines.length === 0;
  const hasIncompleteLines = React.useMemo(
    () =>
      activeLines.some(
        (line) =>
          !getEffectiveProductId(line) ||
          !getEffectiveCargoId(line) ||
          !isWholePositiveNumber(line.quantity),
      ),
    [activeLines],
  );

  const previewResult = useQuery(
    api.adminOrderAmendments.previewAmendmentForAdmin,
    open && step === "preview" && activeLines.length > 0 && !hasIncompleteLines
      ? {
          authUserId,
          orderId: orderId as Id<"orders">,
          draftLines: activeLines.map((line) => ({
            orderItemId: line.orderItemId as Id<"orderItems"> | undefined,
            productId: getEffectiveProductId(line) as Id<"products">,
            cargoId: getEffectiveCargoId(line) as Id<"cargos">,
            quantity: line.quantity,
          })),
        }
      : "skip",
  ) as PreviewData | undefined | null;

  // Initialize lines from order items
  React.useEffect(() => {
    if (!open) {
      initializedOrderIdRef.current = null;
      return;
    }
    // Reactive order-table updates (including the new hold) must not discard
    // the draft the admin is already editing.
    if (initializedOrderIdRef.current === orderId) return;
    initializedOrderIdRef.current = orderId;
    setStep("select");
    setLines(
      orderItems.map((item) => ({
        draftId: createDraftId(),
        orderItemId: item._id,
        originalProductId: item.productId ?? null,
        originalCargoId: item.cargoId ?? null,
        originalProductName: item.product?.name ?? item._id,
        originalProductSlug: item.product?.slug ?? null,
        originalQuantity: item.quantity,
        quantity: item.quantity,
        removed: false,
        isNew: false,
        isSwapping: false,
        selectedProductId: item.productId ?? null,
        selectedProductName: item.product?.name ?? item._id,
        selectedProductSlug: item.product?.slug ?? null,
        selectedCargoId: item.cargoId ?? null,
      })),
    );
    setWarningAccepted(false);
    setFulfillmentStoppedConfirmed(false);
    setDoneStatus(null);
    setPreviewData(null);
    setIsPreviewLoading(false);
    setIsSubmitting(false);
  }, [open, orderId, orderItems]);

  React.useEffect(() => {
    if (!open) return;
    dialogContentRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [open, step]);

  React.useEffect(() => {
    if (previewResult && step === "preview") {
      setPreviewData(previewResult);
      setIsPreviewLoading(false);
    }
  }, [previewResult, step]);

  const updateLine = React.useCallback((draftId: string, patch: Partial<DraftLine>) => {
    setLines((currentLines) =>
      currentLines.map((line) => (line.draftId === draftId ? { ...line, ...patch } : line)),
    );
    setPreviewData(null);
  }, []);

  const removeLine = React.useCallback((draftId: string) => {
    setLines((currentLines) =>
      currentLines.flatMap((line) => {
        if (line.draftId !== draftId) return [line];
        if (line.isNew) return [];
        return [{ ...line, removed: true }];
      }),
    );
    setPreviewData(null);
  }, []);

  const restoreLine = React.useCallback((draftId: string) => {
    updateLine(draftId, { removed: false });
  }, [updateLine]);

  const cancelSwap = React.useCallback(
    (draftId: string) => {
      const line = lines.find((entry) => entry.draftId === draftId);
      if (!line) return;

      updateLine(draftId, {
        isSwapping: false,
        selectedProductId: line.originalProductId,
        selectedProductName: line.originalProductName,
        selectedProductSlug: line.originalProductSlug,
        selectedCargoId: line.originalCargoId,
      });
    },
    [lines, updateLine],
  );

  const addLine = React.useCallback(() => {
    setLines((currentLines) => [
      ...currentLines,
      {
        draftId: createDraftId(),
        originalProductId: null,
        originalCargoId: null,
        originalProductName: "",
        originalProductSlug: null,
        originalQuantity: 0,
        quantity: 1,
        removed: false,
        isNew: true,
        isSwapping: true,
        selectedProductId: null,
        selectedProductName: "",
        selectedProductSlug: null,
        selectedCargoId: null,
      },
    ]);
    setPreviewData(null);
  }, []);

  const handleClose = React.useCallback(() => {
    if (step === "submitting") return;
    onClose();
  }, [onClose, step]);

  const handlePreview = React.useCallback(async () => {
    if (allRemoved) {
      toast.error("You cannot remove all items from the order.");
      return;
    }
    if (hasIncompleteLines) {
      toast.error("Finish each added or swapped item before reviewing changes.");
      return;
    }
    if (!hasChanges) {
      toast.error("No changes detected.");
      return;
    }

    setPreviewData(null);
    setWarningAccepted(false);
    setIsPreviewLoading(true);
    setStep("preview");
  }, [allRemoved, hasChanges, hasIncompleteLines]);

  const handleSubmit = React.useCallback(async () => {
    if (!warningAccepted) {
      toast.error("Please confirm you want to apply these changes.");
      return;
    }

    setIsSubmitting(true);
    setStep("submitting");

    try {
      const result = (await applyAmendment({
        authUserId,
        orderId: orderId as Id<"orders">,
        fulfillmentStoppedConfirmed,
        draftLines: lines
          .filter((line) => !line.removed)
          .map((line) => ({
            orderItemId: line.orderItemId as Id<"orderItems"> | undefined,
            productId: getEffectiveProductId(line) as Id<"products">,
            cargoId: getEffectiveCargoId(line) as Id<"cargos">,
            quantity: line.quantity,
          })),
      })) as { success: boolean; status?: string };

      const nextDoneStatus = result.status === "pending_payment" ? "pending_payment" : "applied";
      setDoneStatus(nextDoneStatus);
      toast.success(
        nextDoneStatus === "pending_payment"
          ? "Order placed on hold awaiting payment"
          : "Order changes saved. Order remains on hold.",
      );
      if (onSuccess) {
        onSuccess();
        return;
      }
      onClose();
      return;
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to save order changes. Please try again.");
      setStep("preview");
      setIsSubmitting(false);
    }
  }, [applyAmendment, authUserId, fulfillmentStoppedConfirmed, lines, onClose, onSuccess, orderId, warningAccepted]);

  const canReviewChanges = !allRemoved && !hasIncompleteLines && hasChanges;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent ref={dialogContentRef} className="max-h-[88vh] overflow-y-auto sm:max-w-[760px] p-6">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <DialogTitle>Edit Order</DialogTitle>
              <DialogDescription className="mt-2">
                Add items, swap coffees, remove lines, and review the price difference before you save.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {eligibility?.canEdit === false && (
          <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {eligibility.reasonMessage}
          </p>
        )}

        {step === "select" && (
          <>
            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                This order is on hold while you edit. It stays on hold if you close without saving.
                Item changes are saved when you select <strong>Save Changes</strong>.
              </div>

              {(changeCounts.added > 0 ||
                changeCounts.swapped > 0 ||
                changeCounts.removed > 0 ||
                changeCounts.quantity_changed > 0) && (
                <div className="flex flex-wrap gap-2">
                  {changeCounts.added > 0 && (
                    <Badge className="bg-emerald-600 text-white">{changeCounts.added} add</Badge>
                  )}
                  {changeCounts.swapped > 0 && (
                    <Badge className="bg-emerald-600 text-white">{changeCounts.swapped} swap</Badge>
                  )}
                  {changeCounts.removed > 0 && (
                    <Badge className="bg-amber-600 text-white">{changeCounts.removed} remove</Badge>
                  )}
                  {changeCounts.quantity_changed > 0 && (
                    <Badge className="bg-slate-700 text-white">
                      {changeCounts.quantity_changed} quantity change
                    </Badge>
                  )}
                </div>
              )}

              <div className="space-y-3">
                {lines.map((line) => (
                  <AmendmentLineEditor
                    key={line.draftId}
                    line={line}
                    products={products}
                    productsLoading={offerListData === undefined}
                    onUpdateLine={updateLine}
                    onRemoveLine={removeLine}
                    onRestoreLine={restoreLine}
                    onCancelSwap={cancelSwap}
                  />
                ))}
              </div>

              {allRemoved && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  Orders must keep at least one item. Restore a line or add a new item to continue.
                </div>
              )}

              {hasIncompleteLines && !allRemoved && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  Finish picking the product and source option for every active line before you review changes.
                </div>
              )}

              <Button
                type="button"
                variant="outline"
                onClick={addLine}
                disabled={offerListData === undefined}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Item
              </Button>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handlePreview} disabled={!canReviewChanges || isPreviewLoading}>
                {isPreviewLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Reviewing...
                  </>
                ) : (
                  "Review Changes"
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "preview" && (
          <>
            <div className="space-y-4 py-2">
              {isPreviewLoading && !previewData ? (
                <div className="flex min-h-[180px] items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                  <div className="flex items-center gap-3 text-sm text-slate-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Reviewing changes…</span>
                  </div>
                </div>
              ) : null}

              {previewData?.lineChanges && previewData.lineChanges.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900">What will change</h3>
                  {previewData.lineChanges.map((change, index) => {
                    const badgeClassName =
                      change.kind === "added"
                        ? "bg-emerald-600 text-white"
                        : change.kind === "removed"
                          ? "bg-amber-600 text-white"
                          : change.kind === "swapped"
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-700 text-white";

                    return (
                      <div key={`${change.kind}-${index}`} className="rounded-lg border border-gray-200 p-3">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <Badge className={badgeClassName}>
                            {change.kind === "quantity_changed"
                              ? "Quantity Changed"
                              : change.kind.charAt(0).toUpperCase() + change.kind.slice(1)}
                          </Badge>
                        </div>

                        {change.before && change.after && change.kind === "swapped" && (
                          <p className="text-sm text-gray-700">
                            {change.before.productName} ({change.before.quantity} bag
                            {change.before.quantity === 1 ? "" : "s"}) will become {change.after.productName} ({change.after.quantity} bag
                            {change.after.quantity === 1 ? "" : "s"}).
                          </p>
                        )}

                        {change.before && change.after && change.kind === "quantity_changed" && (
                          <p className="text-sm text-gray-700">
                            {change.before.productName} changes from {change.before.quantity} bag
                            {change.before.quantity === 1 ? "" : "s"} to {change.after.quantity} bag
                            {change.after.quantity === 1 ? "" : "s"}.
                          </p>
                        )}

                        {change.before && !change.after && (
                          <p className="text-sm text-gray-700">
                            {change.before.productName} will be removed from the order.
                          </p>
                        )}

                        {!change.before && change.after && (
                          <p className="text-sm text-gray-700">
                            {change.after.productName} will be added with {change.after.quantity} bag
                            {change.after.quantity === 1 ? "" : "s"}.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {previewData && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="pb-2 text-left font-medium"></th>
                      <th className="pb-2 text-right font-medium">Before</th>
                      <th className="pb-2 text-right font-medium">After</th>
                      <th className="pb-2 text-right font-medium">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 text-muted-foreground">Item total</td>
                      <td className="py-2 text-right">${previewData.before.subtotal.toFixed(2)}</td>
                      <td className="py-2 text-right">${previewData.after.subtotal.toFixed(2)}</td>
                      <td
                        className={`py-2 text-right ${
                          previewData.itemDelta < 0
                            ? "text-emerald-600"
                            : previewData.itemDelta > 0
                              ? "text-amber-700"
                              : ""
                        }`}
                      >
                        {previewData.itemDelta > 0 ? "+" : previewData.itemDelta < 0 ? "-" : ""}
                        ${Math.abs(previewData.itemDelta).toFixed(2)}
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 text-muted-foreground">Shipping</td>
                      <td className="py-2 text-right">${previewData.before.shippingCost.toFixed(2)}</td>
                      <td className="py-2 text-right">${previewData.after.shippingCost.toFixed(2)}</td>
                      <td
                        className={`py-2 text-right ${
                          previewData.shippingDelta < 0
                            ? "text-emerald-600"
                            : previewData.shippingDelta > 0
                              ? "text-amber-700"
                              : ""
                        }`}
                      >
                        {previewData.shippingDelta > 0 ? "+" : previewData.shippingDelta < 0 ? "-" : ""}
                        ${Math.abs(previewData.shippingDelta).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 font-semibold">Order total</td>
                      <td className="py-2 text-right font-semibold">
                        ${previewData.before.totalAmount.toFixed(2)}
                      </td>
                      <td className="py-2 text-right font-semibold">
                        ${previewData.after.totalAmount.toFixed(2)}
                      </td>
                      <td
                        className={`py-2 text-right font-semibold ${
                          previewData.totalDelta < 0
                            ? "text-emerald-600"
                            : previewData.totalDelta > 0
                              ? "text-amber-700"
                              : ""
                        }`}
                      >
                        {previewData.totalDelta > 0 ? "+" : previewData.totalDelta < 0 ? "-" : ""}
                        ${Math.abs(previewData.totalDelta).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}

              {previewData?.totalDelta !== undefined && previewData.totalDelta > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
                  <p className="font-semibold">Additional payment may be needed</p>
                  <p className="mt-1 text-amber-800">
                    You&apos;re increasing the order by <strong>${previewData.totalDelta.toFixed(2)}</strong>. Credit or scheduled-payment orders will have their outstanding balance updated. Card, bank, and PayPal orders will be placed on hold and the customer will receive a payment request.
                  </p>
                </div>
              )}

              {previewData?.totalDelta !== undefined && previewData.totalDelta < 0 && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  This reduces the order by <strong>${Math.abs(previewData.totalDelta).toFixed(2)}</strong>. Non-credit orders receive Anthem points credit instead of a Stripe or PayPal refund, while credit and scheduled-payment orders simply reduce the outstanding balance.
                </div>
              )}
              {requiresFulfillmentConfirmation && (
                <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <Checkbox
                    id="fulfillment-stopped-confirmed"
                    checked={fulfillmentStoppedConfirmed}
                    onCheckedChange={(checked) => setFulfillmentStoppedConfirmed(checked === true)}
                  />
                  <div>
                    <label htmlFor="fulfillment-stopped-confirmed" className="text-sm font-medium">
                      The warehouse and carrier have stopped fulfillment, and any changed or removed bags are under warehouse control.
                    </label>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Putting an order on hold does not stop a carrier. Coordinate any return before saving.
                      The order stays on hold after changes or payment for Logistics to review before resuming.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-2 border-t pt-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex max-w-2xl items-start gap-3 py-2">
                <Checkbox
                  id="warning-accepted"
                  checked={warningAccepted}
                  onCheckedChange={(checked) => setWarningAccepted(checked === true)}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="warning-accepted"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    I confirm these changes
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Inventory will update immediately. Credit and scheduled-payment orders update their balance only; positive changes on card, bank, or PayPal orders will place the order on hold and send the customer a payment request.
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 justify-end gap-2">
                <Button variant="ghost" onClick={() => setStep("select")} disabled={isSubmitting}>
                  Back
                </Button>
                <Button onClick={handleSubmit} disabled={!warningAccepted || isSubmitting || !previewData || !eligibility?.canEdit || (requiresFulfillmentConfirmation && !fulfillmentStoppedConfirmed)}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}

        {step === "submitting" && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm text-muted-foreground">Applying amendment...</p>
          </div>
        )}

        {step === "done" && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="rounded-full bg-green-100 p-3">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <p className="font-medium">
              {doneStatus === "pending_payment"
                ? "Order placed on hold"
                : "Order amended successfully"}
            </p>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {doneStatus === "pending_payment"
                ? "The order is waiting for amendment payment before it can resume."
                : "Changes have been applied and inventory has been adjusted. The order remains on hold for review before fulfillment resumes."}
            </p>
            <Button onClick={handleClose} className="mt-4">
              Close & Refresh
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
